import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCcw, Send, MessageSquare, Mail, CheckCircle2 } from "lucide-react";

export default function SuperAdminMessages() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [broadcast, setBroadcast] = useState("");
  const [audience, setAudience] = useState("all");
  const [sending, setSending] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/messages");
      setRows(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function sendBroadcast(e) {
    e.preventDefault();
    if (!broadcast.trim()) return;
    if (!window.confirm(`Invio broadcast a "${audience === "all" ? "TUTTI gli utenti" : audience}". Confermi?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/superadmin/broadcast", { message: broadcast.trim(), audience });
      toast.success(`Broadcast inviato a ${data.recipients} utenti`);
      setBroadcast("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  }

  async function markRead(id) {
    try {
      await api.post(`/superadmin/messages/${id}/mark-read`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const unread = rows.filter((r) => !r.read).length;

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight" data-testid="sa-messages-title">Messaggi & Broadcast</h1>
          {unread > 0 && (
            <div className="text-sm text-rose-300 mt-1">{unread} messaggio/i non letti</div>
          )}
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-700 hover:border-rose-500 hover:bg-rose-500/10 text-sm text-slate-300"
        >
          <RefreshCcw size={14}/> Ricarica
        </button>
      </div>

      {/* Broadcast composer */}
      <form onSubmit={sendBroadcast} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-200">
          <Send size={16} className="text-rose-400"/> Invia broadcast agli utenti
        </div>
        <textarea
          data-testid="sa-broadcast-message"
          rows={3}
          value={broadcast}
          onChange={(e) => setBroadcast(e.target.value)}
          placeholder="Il tuo messaggio verrà mostrato come notifica in-app a tutti gli utenti target…"
          className="w-full bg-slate-950 border border-slate-700 rounded-md px-4 py-3 text-sm focus:border-rose-500 outline-none resize-none"
        />
        <div className="flex flex-wrap items-center gap-3">
          <select
            data-testid="sa-broadcast-audience"
            className="bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
          >
            <option value="all">Tutti gli utenti</option>
            <option value="admins">Solo Admin di lega</option>
            <option value="managers">Solo Manager</option>
          </select>
          <button
            type="submit"
            disabled={sending || !broadcast.trim()}
            data-testid="sa-broadcast-send"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-rose-500 hover:bg-rose-400 text-white text-sm font-bold uppercase tracking-wider disabled:opacity-50"
          >
            <Send size={14}/> {sending ? "Invio…" : "Invia"}
          </button>
        </div>
      </form>

      {/* Feedback list */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2 text-sm font-bold text-slate-200">
          <MessageSquare size={16} className="text-sky-400"/> Feedback & Messaggi ricevuti
          <div className="ml-auto text-xs text-slate-500 font-mono">{rows.length} messaggi</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            Nessun messaggio ancora. I feedback dagli utenti compariranno qui.
          </div>
        ) : (
          <ul className="divide-y divide-slate-800">
            {rows.map((m) => (
              <li key={m.message_id} className={`p-4 sm:p-5 ${m.read ? "opacity-60" : ""}`} data-testid={`sa-msg-${m.message_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail size={14} className="text-slate-500"/>
                      <div className="font-bold text-slate-100">{m.user_name || m.user_email || "Anonimo"}</div>
                      <div className="text-xs text-slate-500">{m.user_email}</div>
                      {m.subject && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-bold">
                          {m.subject}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-slate-300 whitespace-pre-wrap">{m.message}</div>
                    {m.reason && (
                      <div className="mt-1 text-xs text-amber-400">Motivo: <span className="font-mono">{m.reason}</span></div>
                    )}
                    <div className="mt-2 text-[11px] text-slate-500 font-mono">
                      {m.league_id ? `Lega: ${m.league_id} · ` : ""}
                      {(m.created_at || "").slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                  {!m.read && (
                    <button
                      onClick={() => markRead(m.message_id)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-600/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-xs"
                      data-testid={`sa-mark-read-${m.message_id}`}
                    >
                      <CheckCircle2 size={12}/> Letto
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
